/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import EditRowFormFields from "./EditRowFormFields";
import Button, { Icons } from "../buttons/Button";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { errorAlert, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import InlineLoader from "../loader/InlineLoader";
import EditGroupFormButtons from "./EditGroupFormButtons";
import {
  EDIT_GROUP_TRANSLATIONS,
  EDIT_LMR_GROUP_TRANSLATIONS,
  EDIT_ROW_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  setShowEditPanel: any;
  org: string;
  panelName: string;
  esRef: string;
  initialGroupState: any;
  initialGroupProfileState: any;
  groupFormFields: any;
  groupProfileFormFields: any;
  header: string;
  IcCheckAPI?: any;
  onGroupUpdate: any;
  onGroupProfileUpdate: any;
  loadDataByRef: any;
  fetchGroupData: any;
  fetchGroupProfileData: any;
  deleteModalItemName?: any;
  onDelete?: any;
  hasDeletePermission?: any;
  hasGroupEditPermission?: boolean;
  hasGroupProfileEditPermission?: boolean;
  hasGroupProfileViewPermission?: boolean;
  refreshTable: any;
  showSmallSidepanel?: boolean;
  transformGroupProfileUpdateBody?: any;
  transformGroupsUpdateBody?: any;
}

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({
  data,
  formFields,
}: {
  data: any;
  formFields: any;
}) => {
  if (!data) return true;
  const isEditFormValid = formFields
    .map((rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });
      return res.reduce((acc: any, el: any) => acc || el);
    })
    .reduce((acc: any, el: any) => acc || el);

  return isEditFormValid;
};

const EditGroup = ({
  setShowEditPanel,
  onDelete,
  esRef,
  org,
  panelName,
  initialGroupState,
  initialGroupProfileState,
  groupFormFields,
  groupProfileFormFields,
  header,
  IcCheckAPI,
  onGroupUpdate,
  onGroupProfileUpdate,
  loadDataByRef,
  fetchGroupData,
  fetchGroupProfileData,
  deleteModalItemName,
  hasDeletePermission = false,
  hasGroupEditPermission = false,
  hasGroupProfileEditPermission = false,
  hasGroupProfileViewPermission = false,
  refreshTable,
  showSmallSidepanel = true,
  transformGroupsUpdateBody,
  transformGroupProfileUpdateBody,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [newdata, setNewData] = useState({
    "LMR-NW-TYPE": "",
    "MAPPED-MCPTT-GID": "",
  });
  const [openEdit, setOpenEdit] = useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const { token } = useContext(AuthContext);
  const [isGroupEditing, setIsGroupEditing] = useState(false);
  const [isGroupProfileEditing, setIsGroupProfileEditing] = useState(false);
  const [groupDataLoading, setGroupDataLoading] = useState(false);
  const [groupProfileDataLoading, setGroupProfileDataLoading] = useState(false);
  const [groupData, setGroupData] = useState(initialGroupState);
  const [groupProfileData, setGroupProfileData] = useState(
    initialGroupProfileState
  );
  const [initialGroupData, setInitialGroupData] = useState(groupData);
  const [initialGroupProfileData, setInitialGroupProfileData] =
    useState(groupProfileData);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const getDataByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await loadDataByRef({
          token,
          org,
          ref,
        });
        const [group, groupProfile] = response;
        setGroupData(group.value);
        setGroupProfileData(groupProfile.value);
        setInitialGroupData(group.value);
        setInitialGroupProfileData(groupProfile.value);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const getLMRDataByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await fetchGroupData({
          token,
          org,
          ref,
        });
        setGroupData(response);
        setInitialGroupData(response);
        if (response["LMR-NW-TYPE"] === "P25-ISSI") {
          const profileResponse = await fetchGroupProfileData({
            token,
            org,
            ref,
          });
          setGroupProfileData(profileResponse);
          setInitialGroupProfileData(profileResponse);
        } else if (response["LMR-NW-TYPE"] === "P25-DFSI") {
          setGroupProfileData({});
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const getGroupDataByRef = async (ref: string) => {
    if (org && token) {
      setGroupDataLoading(true);
      try {
        const groupResponse = await fetchGroupData({
          token,
          org,
          ref,
        });
        setGroupData(groupResponse);
        setInitialGroupData(groupResponse);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setGroupDataLoading(false);
      }
    }
  };

  const getGroupProfileDataByRef = async (ref: string) => {
    if (org && token) {
      setGroupProfileDataLoading(true);
      try {
        const profileResponse = await fetchGroupProfileData({
          token,
          org,
          ref,
        });
        setGroupProfileData(profileResponse);
        setInitialGroupProfileData(profileResponse);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setGroupProfileDataLoading(false);
      }
    }
  };

  // eslint-disable-next-line consistent-return
  const handleUpdateGroupData = async () => {
    setLoading(true);
    try {
      if (panelName === "LMR Group") {
        await IcCheckAPI({
          data: groupData["MAPPED-MCPTT-GID"],
          token,
          org,
        });
        await onGroupUpdate({
          body: transformGroupsUpdateBody(newdata),
          token,
          ref: esRef,
          org,
        });
      } else {
        await IcCheckAPI({
          data: transformGroupsUpdateBody(groupData),
          token,
          org,
        });
        await onGroupUpdate({
          body: transformGroupsUpdateBody(groupData),
          token,
          ref: esRef,
          org,
        });
      }
      refreshTable();
      getGroupDataByRef(esRef);
      successAlert(EDIT_LMR_GROUP_TRANSLATIONS.lmrGroup.successAlertMessage);
      if (panelName === "LMR Group") {
        setNewData({
          "LMR-NW-TYPE": "",
          "MAPPED-MCPTT-GID": "",
        });
      }
      setIsGroupEditing(false);
    } catch (e: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroupProfileData = async () => {
    setLoading(true);
    try {
      await onGroupProfileUpdate({
        body: transformGroupProfileUpdateBody(groupProfileData),
        token,
        ref: esRef,
        org,
      });
      refreshTable();
      getGroupProfileDataByRef(esRef);
      successAlert(
        EDIT_LMR_GROUP_TRANSLATIONS.lmrGroupProfile.successAlertMessage
      );
      setIsGroupProfileEditing(false);
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (panelName === "LMR Group") {
      getLMRDataByRef(esRef);
    } else {
      getDataByRef(esRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esRef]);

  return (
    <>
      <aside
        id="sidepanel"
        data-cy="editGroup-sidepanel"
        className={`sidepanel ${
          showSmallSidepanel
            ? "sidepanel-view small-sidepanel"
            : "sidepanel-edit"
        } fixed z-40 h-full transform transition-transform ease-in duration-150 bg-white`}
      >
        <div className="panel-header">
          <div className="panel-left-side">
            <Button
              kind="ghost"
              className="close-btn"
              icon={Icons.Close}
              onClick={() => setShowEditPanel()}
              dataCy="closeEditGroup-"
            />
            <h5 data-cy="editGroup-panelName"> {panelName} </h5>
          </div>
          <div className="panel-right-side tab-header-buttons">
            <Button
              kind="tertiary"
              className="w-sm"
              label={SIDEPANEL_GENERAL_TRANSLATIONS.buttonLabels.delete}
              disabled={!hasDeletePermission}
              icon={Icons.Delete}
              onClick={() => setOpen(true)}
              dataCy="editGroupDelete-"
            />
          </div>
        </div>
        <div className="panel-content">
          {dataLoading ? (
            <InlineLoader
              description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
            />
          ) : (
            <div className="data-tabs">
              {groupDataLoading ? (
                <InlineLoader
                  description={LOADING_TEXT_TRANSLATIONS.groupLoadingText}
                />
              ) : (
                <div className="inner-datacontent mt-5">
                  <EditGroupFormButtons
                    panelName={header}
                    loading={loading}
                    isEditing={isGroupEditing}
                    setIsEditing={setIsGroupEditing}
                    handleUpdate={handleUpdateGroupData}
                    hasEditPermission={hasGroupEditPermission}
                    revertToInitialState={() => setOpenEdit(true)}
                    isFormInvalid={isFormInvalid({
                      data: groupData,
                      formFields: groupFormFields,
                    })}
                  />
                  <EditRowFormFields
                    data={groupData}
                    isEditing={isGroupEditing}
                    esRef={esRef}
                    setData={setGroupData}
                    formFields={groupFormFields}
                    newData={newdata}
                    setNewData={setNewData}
                    formModule={panelName}
                  />
                </div>
              )}
              {
                // eslint-disable-next-line no-nested-ternary
                hasGroupProfileViewPermission &&
                groupProfileData?.CreationDate ? (
                  groupProfileDataLoading ? (
                    <InlineLoader
                      description={
                        LOADING_TEXT_TRANSLATIONS.groupProfileLoadingText
                      }
                    />
                  ) : (
                    <div className="inner-datacontent mt-5">
                      <EditGroupFormButtons
                        panelName={EDIT_GROUP_TRANSLATIONS.panelName}
                        loading={loading}
                        isEditing={isGroupProfileEditing}
                        setIsEditing={setIsGroupProfileEditing}
                        handleUpdate={handleUpdateGroupProfileData}
                        hasEditPermission={hasGroupProfileEditPermission}
                        revertToInitialState={() => setOpenEditProfile(true)}
                        isFormInvalid={isFormInvalid({
                          data: groupProfileData,
                          formFields: groupProfileFormFields,
                        })}
                      />
                      <EditRowFormFields
                        data={groupProfileData}
                        isEditing={isGroupProfileEditing}
                        esRef={esRef}
                        setData={setGroupProfileData}
                        formFields={groupProfileFormFields}
                      />
                    </div>
                  )
                ) : null
              }
            </div>
          )}
        </div>
      </aside>
      <DeleteModal
        modalHeaderText={EDIT_GROUP_TRANSLATIONS.cancelModal.header(header)}
        modalDescriptionText={EDIT_GROUP_TRANSLATIONS.cancelModal.description}
        onDelete={() => {
          setIsGroupEditing(false);
          setGroupData(initialGroupData);
          setOpenEdit(false);
        }}
        open={openEdit}
        setOpen={setOpenEdit}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_GROUP_TRANSLATIONS.cancelProfileModal.header(
          header
        )}
        modalDescriptionText={
          EDIT_GROUP_TRANSLATIONS.cancelProfileModal.description
        }
        onDelete={() => {
          setIsGroupProfileEditing(false);
          setGroupProfileData(initialGroupProfileData);
          setOpenEditProfile(false);
        }}
        open={openEditProfile}
        setOpen={setOpenEditProfile}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_GROUP_TRANSLATIONS.deleteModal.header(header)}
        modalDescriptionText={EDIT_GROUP_TRANSLATIONS.deleteModal.description(
          header,
          groupData?.[deleteModalItemName]
        )}
        onDelete={() => onDelete(esRef)}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};

export default EditGroup;
